import React, { useState, useMemo } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  alpha,
  useTheme,
  CircularProgress,
  Alert,
  Button
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Schedule as ScheduleIcon,
  Person as PersonIcon,
  Speed as SpeedIcon,
  AccessTime as AccessTimeIcon,
  DirectionsRun as DirectionsRunIcon,
  CheckCircle as CheckCircleIcon,
  Visibility as VisibilityIcon
} from '@mui/icons-material';
import ModalSenhasUsuario from './ModalSenhasUsuario';

const TabelaDesempenhoUsuarios = ({ 
  dados = [], 
  carregando = false, 
  customTheme,
  erro = null,
  filtros = {} 
}) => {
  const theme = useTheme();
  const [orderBy, setOrderBy] = useState('atendimentos');
  const [order, setOrder] = useState('desc');
  const [modalUsuarioAberto, setModalUsuarioAberto] = useState(false);
  const [usuarioSelecionado, setUsuarioSelecionado] = useState(null);

  // Função para lidar com ordenação
  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // Função para abrir modal de senhas do usuário
  const abrirModalUsuario = (usuario) => {
    setUsuarioSelecionado(usuario);
    setModalUsuarioAberto(true);
  };

  // Função para fechar modal
  const fecharModalUsuario = () => {
    setModalUsuarioAberto(false);
    setUsuarioSelecionado(null);
  };

  // Função para lidar com mudança de usuário na navegação
  const handleUsuarioChange = (novoUsuario) => {
    setUsuarioSelecionado(novoUsuario);
  };

  // Função para comparar valores para ordenação
  const getComparator = (order, orderBy) => {
    return order === 'desc'
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy);
  };

  const descendingComparator = (a, b, orderBy) => {
    // Para ordenação por tempo, usar os valores em milissegundos
    if (orderBy === 'tme' || orderBy === 'tmd' || orderBy === 'tma' || orderBy === 'tempoTotal') {
      const aValue = a[`${orderBy}Ms`] || 0;
      const bValue = b[`${orderBy}Ms`] || 0;
      if (bValue < aValue) return -1;
      if (bValue > aValue) return 1;
      return 0;
    }
    
    // Para outros campos, ordenação normal
    if (b[orderBy] < a[orderBy]) return -1;
    if (b[orderBy] > a[orderBy]) return 1;
    return 0;
  };

  // Dados ordenados
  const dadosOrdenados = useMemo(() => {
    return [...dados].sort(getComparator(order, orderBy));
  }, [dados, order, orderBy]);

  // Função para determinar a cor do indicador de desempenho
  const getPerformanceColor = (value, type, allValues) => {
    if (!allValues || allValues.length === 0) return theme.palette.grey[500];
    
    const values = allValues.map(item => item[`${type}Ms`] || 0).filter(v => v > 0);
    if (values.length === 0) return theme.palette.grey[500];
    
    const sorted = [...values].sort((a, b) => a - b);
    const percentile25 = sorted[Math.floor(sorted.length * 0.25)];
    const percentile75 = sorted[Math.floor(sorted.length * 0.75)];
    
    const currentValue = value || 0;
    
    if (currentValue <= percentile25) return '#4caf50'; // Verde - melhor desempenho
    if (currentValue >= percentile75) return '#f44336'; // Vermelho - pior desempenho
    return '#ff9800'; // Laranja - desempenho médio
  };

  // Função para renderizar indicador de desempenho
  const renderPerformanceIndicator = (value, type) => {
    const color = getPerformanceColor(value, type, dadosOrdenados);
    const currentValue = value || 0;
    
    if (currentValue === 0) return null;
    
    const allValues = dadosOrdenados.map(item => item[`${type}Ms`] || 0).filter(v => v > 0);
    if (allValues.length === 0) return null;
    
    const sorted = [...allValues].sort((a, b) => a - b);
    const percentile25 = sorted[Math.floor(sorted.length * 0.25)];
    const percentile75 = sorted[Math.floor(sorted.length * 0.75)];
    
    let icon, tooltipText;
    
    if (currentValue <= percentile25) {
      icon = <TrendingUpIcon sx={{ fontSize: 16 }} />;
      tooltipText = 'Excelente desempenho (25% melhores)';
    } else if (currentValue >= percentile75) {
      icon = <TrendingDownIcon sx={{ fontSize: 16 }} />;
      tooltipText = 'Desempenho pode melhorar (25% piores)';
    } else {
      icon = <ScheduleIcon sx={{ fontSize: 16 }} />;
      tooltipText = 'Desempenho médio';
    }
    
    return (
      <Tooltip title={tooltipText}>
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 20,
            height: 20,
            borderRadius: '50%',
            bgcolor: alpha(color, 0.1),
            color: color,
            ml: 1
          }}
        >
          {icon}
        </Box>
      </Tooltip>
    );
  };

  // Configuração das colunas
  const colunas = [
    {
      id: 'nome',
      label: 'Usuário',
      align: 'left',
      sortable: true,
      icon: <PersonIcon sx={{ fontSize: 16, mr: 1 }} />,
      tooltip: 'Nome do usuário atendente'
    },
    {
      id: 'atendimentos',
      label: 'Atendimentos',
      align: 'center',
      sortable: true,
      icon: <CheckCircleIcon sx={{ fontSize: 16, mr: 1 }} />,
      tooltip: 'Total de senhas atendidas no período'
    },
    {
      id: 'tme',
      label: 'TME',
      align: 'center',
      sortable: true,
      icon: <AccessTimeIcon sx={{ fontSize: 16, mr: 1 }} />,
      tooltip: 'Tempo Médio de Espera - entre emissão e chamada da senha'
    },
    {
      id: 'tmd',
      label: 'TMD',
      align: 'center',
      sortable: true,
      icon: <DirectionsRunIcon sx={{ fontSize: 16, mr: 1 }} />,
      tooltip: 'Tempo Médio de Deslocamento - entre chamada e início do atendimento'
    },
    {
      id: 'tma',
      label: 'TMA',
      align: 'center',
      sortable: true,
      icon: <SpeedIcon sx={{ fontSize: 16, mr: 1 }} />,
      tooltip: 'Tempo Médio de Atendimento - entre início e finalização'
    },
    {
      id: 'tempoTotal',
      label: 'Tempo Total',
      align: 'center',
      sortable: true,
      icon: <ScheduleIcon sx={{ fontSize: 16, mr: 1 }} />,
      tooltip: 'Soma dos três tempos médios (TME + TMD + TMA)'
    },
    {
      id: 'acoes',
      label: 'Ações',
      align: 'center',
      sortable: false,
      icon: <VisibilityIcon sx={{ fontSize: 16, mr: 1 }} />,
      tooltip: 'Ver senhas atendidas pelo usuário'
    }
  ];

  if (carregando) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (erro) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {erro}
      </Alert>
    );
  }

  if (!dados || dados.length === 0) {
    return (
      <Alert severity="info" sx={{ mb: 2 }}>
        Nenhum usuário com atendimentos encontrado no período selecionado.
      </Alert>
    );
  }

  return (
    <>
    <Paper
      elevation={0}
      sx={{
        border: `1px solid ${alpha(customTheme?.palette?.primary?.main || theme.palette.primary.main, 0.2)}`,
        borderRadius: 3,
        overflow: 'hidden'
      }}
    >
      <TableContainer sx={{ maxHeight: 600 }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              {colunas.map((coluna) => (
                <TableCell
                  key={coluna.id}
                  align={coluna.align}
                  sx={{
                    bgcolor: alpha(customTheme?.palette?.primary?.main || theme.palette.primary.main, 0.05),
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    borderBottom: `2px solid ${alpha(customTheme?.palette?.primary?.main || theme.palette.primary.main, 0.1)}`,
                    py: 2
                  }}
                >
                  {coluna.sortable ? (
                    <Tooltip title={coluna.tooltip}>
                      <TableSortLabel
                        active={orderBy === coluna.id}
                        direction={orderBy === coluna.id ? order : 'asc'}
                        onClick={() => handleRequestSort(coluna.id)}
                        sx={{
                          '& .MuiTableSortLabel-icon': {
                            color: `${customTheme?.palette?.primary?.main || theme.palette.primary.main} !important`,
                          },
                          '&:hover': {
                            color: customTheme?.palette?.primary?.main || theme.palette.primary.main,
                          },
                          '&.Mui-active': {
                            color: customTheme?.palette?.primary?.main || theme.palette.primary.main,
                          }
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          {coluna.icon}
                          {coluna.label}
                        </Box>
                      </TableSortLabel>
                    </Tooltip>
                  ) : (
                    <Tooltip title={coluna.tooltip}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: coluna.align }}>
                        {coluna.icon}
                        {coluna.label}
                      </Box>
                    </Tooltip>
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {dadosOrdenados.map((usuario, index) => (
              <TableRow
                key={usuario.id}
                hover
                sx={{
                  '&:hover': {
                    bgcolor: alpha(customTheme?.palette?.primary?.main || theme.palette.primary.main, 0.04),
                  },
                  '&:nth-of-type(even)': {
                    bgcolor: theme.palette.mode === 'dark' ? alpha(theme.palette.grey[50], 0.05) : alpha(theme.palette.grey[900], 0.05),
                  }
                }}
              >
                <TableCell sx={{ fontWeight: 500 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: '50%',
                        bgcolor: alpha(customTheme?.palette?.primary?.main || theme.palette.primary.main, 0.1),
                        color: customTheme?.palette?.primary?.main || theme.palette.primary.main,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mr: 2,
                        fontSize: '0.875rem',
                        fontWeight: 600
                      }}
                    >
                      {usuario.nome.charAt(0).toUpperCase()}
                    </Box>
                    {usuario.nome}
                  </Box>
                </TableCell>
                <TableCell align="center">
                  <Chip
                    label={usuario.atendimentos}
                    size="small"
                    sx={{
                      bgcolor: alpha(customTheme?.palette?.primary?.main || theme.palette.primary.main, 0.1),
                      color: customTheme?.palette?.primary?.main || theme.palette.primary.main,
                      fontWeight: 600
                    }}
                  />
                </TableCell>
                <TableCell align="center">
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography variant="body2" component="span">
                      {usuario.tme}
                    </Typography>
                    {renderPerformanceIndicator(usuario.tmeMs, 'tme')}
                  </Box>
                </TableCell>
                <TableCell align="center">
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography variant="body2" component="span">
                      {usuario.tmd}
                    </Typography>
                    {renderPerformanceIndicator(usuario.tmdMs, 'tmd')}
                  </Box>
                </TableCell>
                <TableCell align="center">
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography variant="body2" component="span">
                      {usuario.tma}
                    </Typography>
                    {renderPerformanceIndicator(usuario.tmaMs, 'tma')}
                  </Box>
                </TableCell>
                <TableCell align="center">
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography 
                      variant="body2" 
                      component="span"
                      sx={{ 
                        fontWeight: 600,
                        color: customTheme?.palette?.primary?.main || theme.palette.primary.main
                      }}
                    >
                      {usuario.tempoTotal}
                    </Typography>
                    {renderPerformanceIndicator(usuario.tempoTotalMs, 'tempoTotal')}
                  </Box>
                </TableCell>
                <TableCell align="center">
                  <Tooltip title="Ver senhas atendidas">
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<VisibilityIcon />}
                      onClick={() => abrirModalUsuario(usuario)}
                      sx={{
                        borderColor: customTheme?.palette?.primary?.main || theme.palette.primary.main,
                        color: customTheme?.palette?.primary?.main || theme.palette.primary.main,
                        '&:hover': {
                          bgcolor: alpha(customTheme?.palette?.primary?.main || theme.palette.primary.main, 0.04),
                          borderColor: customTheme?.palette?.primary?.main || theme.palette.primary.main,
                        },
                        textTransform: 'none',
                        fontSize: '0.75rem'
                      }}
                    >
                      Detalhes
                    </Button>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      
      {/* Rodapé com informações */}
      <Box
        sx={{
          p: 2,
          bgcolor: alpha(customTheme?.palette?.primary?.main || theme.palette.primary.main, 0.06),
          borderTop: `1px solid ${alpha(customTheme?.palette?.primary?.main || theme.palette.primary.main, 0.1)}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        <Typography variant="body2" color="text.secondary">
          {dados.length} usuário{dados.length !== 1 ? 's' : ''} com atendimentos no período
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box
              sx={{
                width: 12,
                height: 12,
                borderRadius: '50%',
                bgcolor: '#4caf50'
              }}
            />
            <Typography variant="caption" color="text.secondary">
              Excelente
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box
              sx={{
                width: 12,
                height: 12,
                borderRadius: '50%',
                bgcolor: '#ff9800'
              }}
            />
            <Typography variant="caption" color="text.secondary">
              Médio
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box
              sx={{
                width: 12,
                height: 12,
                borderRadius: '50%',
                bgcolor: '#f44336'
              }}
            />
            <Typography variant="caption" color="text.secondary">
              Pode melhorar
            </Typography>
          </Box>
        </Box>
      </Box>
    </Paper>
    
    {/* Modal de senhas do usuário */}
    <ModalSenhasUsuario
      open={modalUsuarioAberto}
      onClose={fecharModalUsuario}
      usuario={usuarioSelecionado}
      filtros={filtros}
      customTheme={customTheme}
      usuarios={dadosOrdenados}
      onUsuarioChange={handleUsuarioChange}
    />
  </>
  );
};

export default TabelaDesempenhoUsuarios; 